/**
 * A pencil-thin scroll rail for a box that scrolls inside itself (Duncan 2026-10-24, the sub card's Requests and notes:
 * "that pencil line scrolling thing that we added to the ... schedule on the subcontractor side ... to let the user
 * know that they can scroll down to see more"). A copy of E3 Subs' ScrollRail (e3-subcontractor
 * apps/web/src/components/ScrollRail.tsx, itself the IT Hub's SectionRail), so the two apps' rails look and work alike:
 * - a narrow column of its own left of the box; the bar is the box's content, the thumb the part on screen;
 * - every section has a dot at its true place down the content, with a small label and its count; the one being read
 *   is marked;
 * - click a dot or a label to glide there, click the bar to jump, drag the thumb to scroll;
 * - narrow (a phone): the bar, the thumb and the dots, no labels;
 * - a label too long for the rail wraps onto more lines (Duncan 2026-10-26: "wrap this so the text doesn't disappear
 *   because it's out of room"); the others make room for it.
 * `head`: a sticky header's height in the box (0: none) -- the rail then starts with a band of that height.
 * `width`: the rail's width when it has labels (the punch list's page rail is wider, Duncan 2026-10-26: "Lines to
 * walk 2" ran up to its edge).
 * `page` (Duncan 2026-10-26, the punch list: "a second pencil scroller over to the left of it for all of the sections ...
 * in a small laptop screen you can see where you're scrolling"): the rail follows the window's scroll, not a box's; its
 * items' `top` are then places down the page. Its parent keeps it in view (sticky).
 * `activeId` (Duncan 2026-10-30, the task windows' rail: "highlighted by where the mouse is"): its owner marks one -- the
 * section under the pointer -- instead of the one in view. `floating`: a rail beside a window over a dimmed page, no card
 * behind it -- white ink with a soft shadow, the one marked a pill in the theme's green ("we need that green on theme
 * color, I just need it to show more").
 */
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

const LABEL_H = 14; // a label's line
const GAP = 3;
const WIDTH = 108;
const WIDTH_NARROW = 20;

/** What the rail reads and moves: a box that scrolls, or the page. */
function scrollerOf(box, page) {
  if (page) {
    return {
      el: document.body, target: window,
      height: () => document.documentElement.scrollHeight, seen: () => window.innerHeight, y: () => window.scrollY,
      setY: (v) => window.scrollTo(0, v), glide: (top) => window.scrollTo({ top, behavior: 'smooth' }),
    };
  }
  const el = box && box.current;
  if (!el) return null;
  return {
    el, target: el,
    height: () => el.scrollHeight, seen: () => el.clientHeight, y: () => el.scrollTop,
    setY: (v) => { el.scrollTop = v; }, glide: (top) => el.scrollTo({ top, behavior: 'smooth' }),
  };
}

export default function ScrollRail({ box, items, head = 0, title, narrow, page = false, width: labelledWidth = WIDTH, activeId = null, floating = false }) {
  const railRef = useRef(null);
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const key = items.map((i) => `${i.id}:${i.top}:${i.count}`).join('|');
  const [active, setActive] = useState(items[0] ? items[0].id : '');
  const activeRef = useRef(active);
  activeRef.current = active;
  const pinnedUntil = useRef(0); // after a click the choice holds while the box glides there
  const [geo, setGeo] = useState(null);
  const width = narrow ? WIDTH_NARROW : labelledWidth;
  const x = narrow ? 5 : 12; // where the bar's strip starts
  // How tall each label wraps, read after every render; when it changes, the labels are laid out again.
  const labelEls = useRef(new Map());
  const labelHs = useRef(new Map());
  const laidOutWith = useRef('');
  const relayout = useRef(null);

  useEffect(() => {
    const sc = scrollerOf(box, page);
    const rail = railRef.current;
    if (!sc || !rail) return undefined;
    const { el } = sc;
    let raf = 0;
    const measure = () => {
      raf = 0;
      const list = itemsRef.current;
      const railH = Math.max(40, rail.clientHeight);
      const total = Math.max(1, sc.height() - head); // the content, all of it
      const seen = Math.max(0, sc.seen() - head); // the content in view
      const y = sc.y();
      const marks = list.map((i) => ((i.top - head) / total) * railH);
      const thumbH = Math.min(railH, Math.max(18, (seen / total) * railH));
      const thumbTop = Math.min(railH - thumbH, (y / total) * railH);

      // Which section is being read: the last whose start has risen to the top, under the header.
      if (list.length && pinnedUntil.current <= Date.now()) {
        const at = list.map((i) => i.top - y);
        let idx = 0;
        at.forEach((a, k) => { if (a <= head + LABEL_H) idx = k; });
        if (y + sc.seen() >= sc.height() - 2) {
          // At the bottom: keep the current pick if its start is in view, else the first in view.
          const inView = (k) => at[k] >= head && at[k] < sc.seen();
          const cur = list.findIndex((i) => i.id === activeRef.current);
          if (cur >= 0 && inView(cur)) idx = cur;
          else {
            const first = at.findIndex((_, k) => inView(k));
            if (first >= 0) idx = first;
          }
        }
        setActive(list[idx].id);
      }

      // Labels: the first line centred on its dot, pushed apart so they never overlap, kept inside the rail.
      const n = marks.length;
      const hs = list.map((i) => labelHs.current.get(i.id) || LABEL_H);
      laidOutWith.current = hs.join(',');
      const showLabels = !narrow && hs.reduce((s, h) => s + h + GAP, 0) <= railH;
      const labelTop = marks.map((m, k) => Math.min(railH - hs[k], Math.max(0, m - LABEL_H / 2)));
      if (showLabels) {
        for (let k = 1; k < n; k++) labelTop[k] = Math.max(labelTop[k], labelTop[k - 1] + hs[k - 1] + GAP);
        for (let k = n - 1; k >= 0; k--) {
          const limit = (k === n - 1 ? railH : labelTop[k + 1] - GAP) - hs[k];
          if (labelTop[k] > limit) labelTop[k] = limit;
        }
      }
      setGeo({ railH, marks, labelTop, showLabels, thumbTop, thumbH });
    };
    relayout.current = measure;
    const onChange = () => { if (!raf) raf = requestAnimationFrame(measure); };
    sc.target.addEventListener('scroll', onChange, { passive: true });
    if (page) window.addEventListener('resize', onChange);
    // The box, the rail, and the box's content can each change size.
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(onChange);
    if (ro) {
      ro.observe(el);
      ro.observe(rail);
      if (el.firstElementChild) ro.observe(el.firstElementChild);
    }
    measure();
    return () => {
      relayout.current = null;
      sc.target.removeEventListener('scroll', onChange);
      if (page) window.removeEventListener('resize', onChange);
      if (ro) ro.disconnect();
      if (raf) cancelAnimationFrame(raf);
    };
  }, [key, box, head, narrow, page]);

  // A label that wraps onto another line (or back) -- new text, or the one being read turning bold -- moves the
  // others: lay them out again before the rail is painted.
  useLayoutEffect(() => {
    if (narrow) return;
    labelHs.current = new Map(items.map((it) => {
      const el = labelEls.current.get(it.id);
      return [it.id, el ? el.offsetHeight : LABEL_H];
    }));
    const now = items.map((it) => labelHs.current.get(it.id)).join(',');
    if (now !== laidOutWith.current && relayout.current) relayout.current();
  });

  const jump = (it) => {
    const sc = scrollerOf(box, page);
    if (!sc) return;
    pinnedUntil.current = Date.now() + 900;
    setActive(it.id);
    sc.glide(Math.max(0, it.top - head));
  };
  const onTrackClick = (e) => {
    const sc = scrollerOf(box, page);
    const rail = railRef.current;
    if (!sc || !rail) return;
    const r = rail.getBoundingClientRect();
    const frac = (e.clientY - r.top) / r.height;
    pinnedUntil.current = 0;
    sc.glide(frac * (sc.height() - head) - (sc.seen() - head) / 2);
  };
  const onThumbDown = (e) => {
    const sc = scrollerOf(box, page);
    const rail = railRef.current;
    if (!sc || !rail) return;
    e.preventDefault();
    e.stopPropagation();
    const startY = e.clientY;
    const startScroll = sc.y();
    const railH = rail.getBoundingClientRect().height;
    const total = sc.height() - head;
    pinnedUntil.current = 0;
    const move = (ev) => { sc.setY(startScroll + ((ev.clientY - startY) / railH) * total); };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  return (
    <div style={{ flex: 'none', width, display: 'flex', flexDirection: 'column', boxSizing: 'border-box', borderRight: '1px solid var(--border-subtle)', userSelect: 'none' }}>
      {head > 0 && (
        <div style={{
          flex: 'none', height: head, boxSizing: 'border-box', display: 'flex', alignItems: 'flex-end', padding: '0 10px 10px',
          background: 'var(--bg-surface-2)', borderBottom: '1px solid var(--border-subtle)', fontSize: 10.5, fontWeight: 700,
          letterSpacing: '0.075em', textTransform: 'uppercase', color: 'var(--text-3)',
        }}>
          {!narrow && title}
        </div>
      )}
      <div ref={railRef} aria-label={title || 'Sections'} style={{ position: 'relative', flex: 1, margin: '12px 0' }}>
        {/* the bar, with a wider strip around it to click on */}
        <div onClick={onTrackClick} title="Click to jump" style={{ position: 'absolute', left: x - 4, top: 0, bottom: 0, width: 14, cursor: 'pointer' }}>
          <div style={{ position: 'absolute', left: 7, top: 0, bottom: 0, width: 2, borderRadius: 1, background: floating ? 'rgba(255,255,255,0.7)' : 'var(--border-strong)', boxShadow: floating ? '0 0 3px rgba(0,0,0,0.35)' : 'none' }} />
        </div>
        {geo && (
          <div onPointerDown={onThumbDown} title="Drag to scroll"
            style={{
              position: 'absolute', left: x + 1, top: geo.thumbTop, height: geo.thumbH, width: 6, borderRadius: 3,
              background: 'var(--accent)', opacity: floating ? 0.95 : 0.35, cursor: 'grab', touchAction: 'none',
              boxShadow: floating ? '0 0 0 1.5px rgba(255,255,255,0.9), 0 1px 4px rgba(0,0,0,0.35)' : 'none',
            }} />
        )}
        {geo && items.map((it, k) => {
          const on = it.id === (activeId || active);
          const y = geo.marks[k] || 0;
          const labelled = !narrow && (geo.showLabels || on);
          return (
            <button key={it.id} type="button" title={it.tip || `${it.label} ${it.count}`} aria-label={`Go to ${it.tip || it.label}`}
              aria-current={on ? 'true' : undefined} onClick={(e) => { e.stopPropagation(); jump(it); }}
              style={{ position: 'absolute', left: 0, top: 0, width, height: 0, padding: 0, border: 'none', background: 'none', font: 'inherit', cursor: 'pointer' }}>
              <span style={{
                position: 'absolute', left: on ? x - (floating ? 2 : 1) : x + 1, top: y - (on ? (floating ? 6 : 5) : 3), width: on ? (floating ? 12 : 10) : 6, height: on ? (floating ? 12 : 10) : 6, borderRadius: 6,
                background: on ? 'var(--accent)' : (floating ? '#fff' : 'var(--border-strong)'),
                boxShadow: on ? (floating ? '0 0 0 2px #fff, 0 1px 5px rgba(0,0,0,0.4)' : '0 0 0 2px var(--accent-ring)') : (floating ? '0 1px 3px rgba(0,0,0,0.4)' : 'none'),
              }} />
              {/* every label is there to be measured; the ones without room stay hidden */}
              {!narrow && (
                <span ref={(el) => { if (el) labelEls.current.set(it.id, el); else labelEls.current.delete(it.id); }} style={{
                  position: 'absolute', left: x + 14, top: geo.labelTop[k], lineHeight: `${LABEL_H}px`, maxWidth: width - x - 16,
                  fontSize: 11, fontWeight: on ? 700 : 500, color: on ? 'var(--accent-text)' : 'var(--text-3)', whiteSpace: 'normal',
                  overflowWrap: 'anywhere', textAlign: 'left', visibility: labelled ? 'visible' : 'hidden',
                  ...(floating ? (on
                    ? { color: '#fff', background: 'var(--accent)', borderRadius: 999, padding: '1px 8px', marginLeft: -4, marginTop: -1,
                      boxShadow: '0 0 0 1.5px rgba(255,255,255,0.9), 0 2px 6px rgba(0,0,0,0.3)' }
                    : { color: '#fff', textShadow: '0 1px 2px rgba(0,0,0,0.6), 0 0 8px rgba(0,0,0,0.35)' }) : {}),
                }}>
                  {it.label}{' '}
                  <span className="mono" style={{ fontSize: 10, color: on ? 'var(--accent-text)' : 'var(--text-4)', whiteSpace: 'nowrap', lineHeight: 1 }}>{it.count}</span>
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
